
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Linkedin, Instagram } from 'lucide-react';
import { useAnalytics } from '@/hooks/useAnalytics';
import CookieSettings from '@/components/common/CookieSettings';

const FooterSection = () => {
  const { trackSocialShare, trackNavigation } = useAnalytics();
  const location = useLocation();
  const navigate = useNavigate();
  const isHomepage = location.pathname === "/" || location.pathname === "/homepage";
  const isAboutPage = location.pathname === "/about";

  const handleSocialClick = (platform: string) => {
    trackSocialShare(platform, 'footer_link');
  };

  const handleNavClick = (destination: string) => {
    trackNavigation(destination, 'footer');
  };

  const scrollToSection = (sectionId: string) => {
    if (isHomepage) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/#' + sectionId);
    }
  };

  return (
    <footer className="py-8 bg-[#F7F5F2] text-[#6C4F77]">
      <div className="section-container">
        <div className="flex items-center justify-center mr-4">
            <Link to="/" className="flex items-center" onClick={() => trackNavigation('home', 'header_logo')}>
              <div className="relative h-16 w-16 m-[-5px]">
                <img
                  src="/wabisabi-logo-transparent.png"
                  alt="Wabisabi Logo"
                  className="h-16 w-16 object-contain absolute top-1"
                />
              </div>
              <span className="text-xl font-bold text-[#362652]">Wabisabi</span>
            </Link>
          </div>
        <div className="text-center">

          <span className="text-[16px] text-[#6C4F77]/80 mb-4 block">
            Remember what matters
          </span>

          {/* Social Media Links */}
          <div className="flex justify-center gap-4 mb-6">
            <a
              href="https://www.linkedin.com/company/appwabisabi/about/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-10 h-10 bg-[#6C4F77]/10 hover:bg-[#6C4F77]/20 rounded-full transition-all duration-300 group hover:scale-110"
              aria-label="Follow us on LinkedIn"
              onClick={() => handleSocialClick('linkedin')}
            >
              <Linkedin className="w-5 h-5 text-[#6C4F77] group-hover:text-[#F4978E] transition-colors" />
            </a>
            <a
              href="https://www.instagram.com/appwabisabi/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-10 h-10 bg-[#6C4F77]/10 hover:bg-[#6C4F77]/20 rounded-full transition-all duration-300 group hover:scale-110"
              aria-label="Follow us on Instagram"
              onClick={() => handleSocialClick('instagram')}
            >
              <Instagram className="w-5 h-5 text-[#6C4F77] group-hover:text-[#F4978E] transition-colors" />
            </a>
          </div>

          {/* Footer Links */}
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 mb-4 text-[16px] px-4">
            {/* Navigation Links */}
            {isHomepage ? (
              <>
                <button
                  onClick={() => scrollToSection('home')}
                  className="text-[#6C4F77] hover:text-[#F4978E] transition-colors"
                >
                  Home
                </button>
                <span className="text-[#6C4F77]/40 hidden sm:inline">•</span>
                <button
                  onClick={() => scrollToSection('how-it-works')}
                  className="text-[#6C4F77] hover:text-[#F4978E] transition-colors"
                >
                  How it Works
                </button>
                <span className="text-[#6C4F77]/40 hidden sm:inline">•</span>
                <button
                  onClick={() => scrollToSection('our-values')}
                  className="text-[#6C4F77] hover:text-[#F4978E] transition-colors"
                >
                  Our Values
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/"
                  className="text-[#6C4F77] hover:text-[#F4978E] transition-colors"
                  onClick={() => handleNavClick('home')}
                >
                  Home
                </Link>
                <span className="text-[#6C4F77]/40 hidden sm:inline">•</span>
                <button
                  onClick={() => scrollToSection('how-it-works')}
                  className="text-[#6C4F77] hover:text-[#F4978E] transition-colors"
                >
                  How it Works
                </button>
                <span className="text-[#6C4F77]/40 hidden sm:inline">•</span>
                <button
                  onClick={() => scrollToSection('our-values')}
                  className="text-[#6C4F77] hover:text-[#F4978E] transition-colors"
                >
                  Our Values
                </button>
              </>
            )}
            {!isAboutPage && (
              <>
                <span className="text-[#6C4F77]/40 hidden sm:inline">•</span>
                <Link
                  to="/about"
                  className="text-[#6C4F77] hover:text-[#F4978E] transition-colors"
                  onClick={() => handleNavClick('about')}
                >
                  About
                </Link>
              </>
            )}
            <span className="text-[#6C4F77]/40 hidden sm:inline">•</span>
            <Link
              to="/privacy-policy"
              className="text-[#6C4F77] hover:text-[#F4978E] transition-colors"
              onClick={() => handleNavClick('privacy-policy')}
            >
              Privacy Policy
            </Link>
            <span className="text-[#6C4F77]/40 hidden sm:inline">•</span>
            <Link
              to="/terms-of-service"
              className="text-[#6C4F77] hover:text-[#F4978E] transition-colors"
              onClick={() => handleNavClick('terms-of-service')}
            >
              Terms of Service
            </Link>
            <span className="text-[#6C4F77]/40 hidden sm:inline">•</span>
            <CookieSettings />
          </div>

          <span className="text-[14px] text-[#6C4F77]/60">
            &copy; {new Date().getFullYear()} Wabisabi. Made with 💜 for deeper connections.
          </span>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;
